import { maybe, rd } from '@passionware/monads';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { Button } from 'v5/design-system/Button';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxQueryRenderer
} from 'v5/design-system/Combobox';
import {
  FilterWidgetProps,
  PerFieldConfigBase,
  FilterGroupDefinition
} from './FilterWidget.types';

export interface FilterWidgetAddControlProps<
  FieldConfig extends PerFieldConfigBase
> {
  config: FieldConfig;
  order: (keyof FieldConfig)[];
  groups?: FilterGroupDefinition[];
  value: FilterWidgetProps<FieldConfig>['value'];
  onValueChange: FilterWidgetProps<FieldConfig>['onValueChange'];
  hiddenFilters?: (keyof FieldConfig)[];
  addFilterButtonRef?: React.RefObject<HTMLButtonElement>;
  containerRef?: React.RefObject<HTMLDivElement>;
  onFilterAdded?: (field: keyof FieldConfig) => void;
}

export function FilterWidgetAddControl<FieldConfig extends PerFieldConfigBase>({
  config,
  order,
  groups = [],
  value,
  onValueChange,
  hiddenFilters = [],
  addFilterButtonRef,
  containerRef,
  onFilterAdded
}: FilterWidgetAddControlProps<FieldConfig>) {
  const { t } = useTranslation('patterns');
  const [open, setOpen] = useState(false);

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild>
        <Button
          ref={addFilterButtonRef}
          variant="secondary"
          size="sm"
          layout="icon"
          aria-label={t('FilterWidgetAddControl.manageFilters')}
        >
          <FunnelIcon className="size-4" />
        </Button>
      </ComboboxTrigger>
      <ComboboxContent className="min-w-60">
        <ComboboxInput
          placeholder={t('FilterWidgetAddControl.searchPlaceholder')}
        />
        <ComboboxList className="with-styled-scrollbars with-styled-scrollbars__transparent">
          <ComboboxQueryRenderer
            emptyContent={t('FilterWidgetAddControl.emptySearch')}
          >
            {query => {
              const normalizedQuery = query.trim().toLowerCase();

              // Create ordered groups based on the position of their first child in the order array
              const orderedGroups = groups
                .map(group => ({
                  ...group,
                  firstChildIndex: Math.min(
                    ...group.children
                      .map(child => order.indexOf(child as keyof FieldConfig))
                      .filter(index => index >= 0)
                  )
                }))
                .filter(group => group.firstChildIndex >= 0)
                .sort((a, b) => a.firstChildIndex - b.firstChildIndex);

              // Collect all fields that are part of groups
              const groupedFields = new Set(
                orderedGroups.flatMap(group => group.children)
              );

              // Create a unified list of groups (including ungrouped fields as a special group)
              const allGroups = [
                ...orderedGroups,
                // Add ungrouped fields as a group (without heading for cleaner UI)
                ...(order.some(field => !groupedFields.has(field as string))
                  ? [
                      {
                        name: '',
                        children: order.filter(
                          field => !groupedFields.has(field as string)
                        ),
                        firstChildIndex: Math.min(
                          ...order
                            .map((field, index) =>
                              !groupedFields.has(field as string)
                                ? index
                                : Infinity
                            )
                            .filter(index => index !== Infinity)
                        )
                      }
                    ]
                  : [])
              ].sort((a, b) => a.firstChildIndex - b.firstChildIndex);

              // Render all groups uniformly
              const allOptions = allGroups
                .map(group => {
                  const groupFields = group.children.filter(field => {
                    const itemConfig = config[field as keyof FieldConfig];
                    if (!itemConfig) return false;
                    // Filter out hidden filters
                    if (hiddenFilters.includes(String(field))) return false;
                    // Filter out active filters - only show addable ones
                    if (maybe.isPresent(value[field as keyof FieldConfig]))
                      return false;
                    if (!normalizedQuery) return true;
                    return itemConfig.label
                      .toLowerCase()
                      .includes(normalizedQuery);
                  });

                  if (groupFields.length === 0) return null;

                  // Only show group heading if group has a name
                  const content = (
                    <>
                      {groupFields.map(field => {
                        const itemConfig = config[field as keyof FieldConfig];
                        if (!itemConfig) return null;
                        return (
                          <ComboboxItem
                            key={String(field)}
                            compact
                            value={String(field)}
                            closeOnSelect={true}
                            disabled={itemConfig.disabled}
                            onSelect={() => {
                              // Add the filter
                              onValueChange({
                                ...value,
                                [field]: itemConfig.createDefaultValue()
                              });
                              // Close the combobox
                              setOpen(false);
                              // Notify parent that a filter was added
                              onFilterAdded?.(field as keyof FieldConfig);
                            }}
                          >
                            {itemConfig.icon && (
                              <span className="mr-2 inline-flex size-4 items-center justify-center">
                                {itemConfig.icon}
                              </span>
                            )}
                            <span>
                              {itemConfig.labelContent ?? itemConfig.label}
                            </span>
                          </ComboboxItem>
                        );
                      })}
                    </>
                  );

                  return group.name ? (
                    <ComboboxGroup key={group.name} heading={group.name}>
                      {content}
                    </ComboboxGroup>
                  ) : (
                    // Ungrouped fields rendered without a group wrapper
                    content
                  );
                })
                .filter(Boolean);

              return rd.of(allOptions);
            }}
          </ComboboxQueryRenderer>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
